import React, { useCallback, useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CHEERS_EVENT, countUnseen } from "../lib/cheerSeen";
import { useAuth } from "../context/AuthContext";

// Header heart badge: unseen cheers for the active child (signed-in, server-backed children only).
const CheerBadge = ({ child, variant = "desktop" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const childId = child?.id;
  const enabled = !!user && !!childId && !!child?.ownerId;

  const refresh = useCallback(() => {
    if (!enabled) { setCount(0); return; }
    api.get(`/children/${childId}/cheers`).then((r) => setCount(countUnseen(childId, r.data, user.id))).catch(() => setCount(0));
  }, [enabled, childId, user?.id]);

  useEffect(() => {
    refresh();
    const onChange = (e) => { if (!e.detail?.childId || e.detail.childId === childId) refresh(); };
    window.addEventListener(CHEERS_EVENT, onChange);
    const t = variant === "desktop" ? setInterval(refresh, 60000) : null; // only one instance polls
    return () => { window.removeEventListener(CHEERS_EVENT, onChange); if (t) clearInterval(t); };
  }, [refresh, childId, variant]);

  if (!enabled) return null;
  const has = count > 0;
  const suffix = variant === "desktop" ? "" : `-${variant}`;
  return (
    <button
      type="button"
      onClick={() => navigate(`/family#child-${childId}`)}
      className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${has ? "bg-[#fff0f5] text-[#e5356f] hover:bg-[#ffe0ec]" : "text-gray-300 hover:text-[#e5356f] hover:bg-[#fff0f5]"}`}
      title={has ? `${count} new cheer${count === 1 ? "" : "s"} for ${child.panda.name}` : `No new cheers for ${child.panda.name}`}
      aria-label={has ? `${count} new cheers` : "Cheers"}
      data-testid={`cheer-badge${suffix}`}
    >
      <Heart size={20} fill={has ? "#e5356f" : "none"} className={has ? "heart-pop" : ""} />
      {has && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#e5356f] text-white text-[11px] font-bold flex items-center justify-center border-2 border-white" data-testid={`cheer-badge-count${suffix}`}>
          {count > 9 ? "9+" : count}
        </span>
      )}
    </button>
  );
};

export default CheerBadge;
